import cx from 'classnames';
import {filter, includes, isEmpty, isUndefined, map, transform, set, forEach} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, InputFormik, MultiInputFormik, NumericFieldFormik, Select, SelectFormik, ToggleFormik} from 'components';
import {CustomField, CustomFieldType} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey} from 'utils';

import {
    ATTRIBUTE_DEFAULT_VALUES, ATTRIBUTE_OPTIONS, ATTRIBUTE_TYPES, FORM_FIELD_KEYS,
    NUMBER_ATTRIBUTE_NAMES, READ_ONLY_ATTRIBUTES, READ_ONLY_FIELD_NAMES, STRING_ATTRIBUTE_NAMES
} from '../constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.IN_SESSION.USER_INFO_SCHEMA_BUILDER;

type Props = {
    last: boolean;
    path: string;
    item?: CustomField;
    renderDeleteAction?: () => React.ReactNode;
    onAdd: (path: string, value: any) => void;
    onRemove: (path: string) => void;
}

export const CustomFieldEditor: React.FC<Props> = ({item, path, last, renderDeleteAction, onAdd, onRemove}) => {
    const {t} = useTranslation();

    const typeOptions = useMemo(() => [CustomFieldType.String, CustomFieldType.Number].map((itemType) => ({
        id: itemType,
        name: t(T_KEYS.TYPES[buildTranslationKey(itemType)])
    })), [t]);

    const restAttributes = useMemo(() => {
        const possibleOptions = Object.values(FORM_FIELD_KEYS)
            .filter((attribute) =>
                ![
                    FORM_FIELD_KEYS.NAME,
                    FORM_FIELD_KEYS.TITLE,
                    FORM_FIELD_KEYS.TYPE
                ].includes(attribute));

        return filter(Object.keys(item || {}), (attribute) => possibleOptions.includes(attribute));
    }, [item]);

    const booleanRules = useMemo(() =>
        filter(restAttributes, (attr) => ATTRIBUTE_TYPES[attr] === 'boolean'),
    [restAttributes]);

    const numericRules = useMemo(() =>
        filter(restAttributes, (attr) => ATTRIBUTE_TYPES[attr] === 'number'),
    [restAttributes]);

    const selectAndStringRules = useMemo(() =>
        filter(restAttributes, (attr) => ATTRIBUTE_TYPES[attr] === 'string'),
    [restAttributes]);

    const enumRules = useMemo(() =>
        filter(restAttributes, (attr) => ATTRIBUTE_TYPES[attr] === 'string[]'),
    [restAttributes]);

    const allValidationRules = useMemo(() => [
        FORM_FIELD_KEYS.REQUIRED,
        FORM_FIELD_KEYS.NULLABLE,
        ...(item?.type === CustomFieldType.String) ? STRING_ATTRIBUTE_NAMES : [],
        ...(item?.type === CustomFieldType.Number) ? NUMBER_ATTRIBUTE_NAMES : [],
    ], [item?.type]);

    const availableValidationRuleOptions = useMemo(() =>
        transform(allValidationRules, (acc, validationRule) => {
            if (isUndefined(item?.[validationRule])) {
                acc.push({
                    id: validationRule,
                    name: t(T_KEYS.LABELS[buildTranslationKey(validationRule)])
                });
            }
        }, []), [t, item, allValidationRules]);

    const renderAttributeFormElement = useCallback((
        attribute: keyof typeof FORM_FIELD_KEYS,
        isReadonly: boolean,
        onRemoveRule?: () => void,
    ) => {
        const tKey = buildTranslationKey(attribute);
        const label = t(T_KEYS.LABELS[tKey]);
        const type = ATTRIBUTE_TYPES[attribute];
        const name = `${path}.${attribute}`;
        const options = ATTRIBUTE_OPTIONS[attribute];

        if (type === 'boolean') {
            return (
                <ToggleFormik
                    name={name}
                    label={label}
                    size='small'
                    labelPlacement='right'
                    className='tw-my-1'
                    disabled={isReadonly}
                />
            );
        }

        if (!isEmpty(options)) {
            const selectOptions = map(options, (option) => ({
                id: option,
                name: t(T_KEYS.OPTIONS[tKey][buildTranslationKey(option)])
            }));
            return (
                <SelectFormik
                    className='tw-w-full tw-my-1'
                    placeholder={label}
                    name={name}
                    options={selectOptions}
                    valueAs='id'
                    disabled={isReadonly}
                />
            );
        }

        if (type === 'number') {
            return (
                <NumericFieldFormik
                    validate={!last}
                    name={name}
                    label={label}
                    disabled={isReadonly}
                    className='tw-w-full tw-my-1'
                />
            );
        }

        if (type === 'string[]') {
            return (
                <div className='tw-flex tw-flex-wrap'>
                    <MultiInputFormik
                        name={name}
                        itemsPath={attribute}
                        defaultValue=''
                        values={item}
                        isValid={(_item) => !!_item}
                    >
                        {({index, item: _item, last: _last, path: _path, onRemove: _onRemove, renderActions}) => (
                            <div
                                key={index}
                                className={cx({
                                    'tw-order-2': !_last,
                                    'tw-order-1 tw-w-full tw-flex-grow': _last,
                                })}
                            >
                                {_last ? (
                                    <div className='tw-w-full tw-flex tw-items-start tw-mb-2'>
                                        {onRemoveRule && (
                                            <div className='tw-mr-1 tw-mt-8 tw-flex-shrink-0'>
                                                <Icon
                                                    name='minus-circle'
                                                    color='alert'
                                                    size='xs'
                                                    button
                                                    onClick={onRemoveRule}
                                                />
                                            </div>
                                        )}
                                        <InputFormik
                                            className='tw-w-full tw-my-1'
                                            name={`${path}.${_path}`}
                                            label={label}
                                            disabled={isReadonly}
                                        />
                                        {!isReadonly &&
                                            <div className='tw-w-10 tw-ml-3 tw-mt-8 -md:tw-mt-9'>
                                                {renderActions({index, item: _item})}
                                            </div>}
                                    </div>
                                ) : (
                                    <div className='tw-flex tw-flex-center tw-border tw-rounded-full tw-m-1 tw-px-2 tw-py-1'>
                                        {_item}
                                        {!isReadonly &&
                                            <div className='tw-ml-2'>
                                                <Icon button name='close' onClick={() => _onRemove({index, item: _item})} />
                                            </div>}
                                    </div>
                                )}
                            </div>
                        )}
                    </MultiInputFormik>
                </div>
            );
        }

        if (type === 'string') {
            return (
                <InputFormik
                    validate={!last}
                    name={name}
                    label={label}
                    disabled={isReadonly}
                    className='tw-w-full tw-my-1'
                />
            );
        }

    }, [item, path, last, t]);

    const isReadOnlyAttribute = useCallback((attribute: keyof typeof FORM_FIELD_KEYS) => {
        const name = `${item.name}.${attribute}`;
        return includes(READ_ONLY_ATTRIBUTES, name);
    }, [item]);

    const renderRuleWithRemove = useCallback((attribute: keyof typeof FORM_FIELD_KEYS) => {
        const isReadonly = isReadOnlyAttribute(attribute);
        const isBoolean = ATTRIBUTE_TYPES[attribute] === 'boolean';
        const attributeValue = item[attribute];

        if (isUndefined(attributeValue)) {
            return null;
        }

        return (
            <div
                key={attribute}
                className={cx('tw-flex tw-gap-1', {
                    'tw-items-center': isBoolean,
                    'tw-items-start': !isBoolean,
                })}
            >
                {!isReadonly && (
                    <Icon
                        name='minus-circle'
                        color='alert'
                        size='xs'
                        button
                        className={cx('tw-flex-shrink-0', {
                            'tw-mt-8': !isBoolean,
                        })}
                        onClick={() => onRemove(`${path}.${attribute}`)}
                    />
                )}
                <div className='tw-flex-grow'>
                    {renderAttributeFormElement(attribute, isReadonly)}
                </div>
            </div>
        );
    }, [item, path, renderAttributeFormElement, isReadOnlyAttribute, onRemove]);

    const handleAddAttributeDirectly = useCallback((value: string) => {
        if (!value || !isUndefined(item?.[value])) {
            return;
        }
        onAdd(`${path}.${value}`, ATTRIBUTE_DEFAULT_VALUES[value]);
    }, [item, path, onAdd]);

    useEffect(() => {
        if (item?.type === CustomFieldType.Number) {
            forEach(STRING_ATTRIBUTE_NAMES, (attribute) => {
                set(item, attribute, undefined);
            });
        }
        if (item?.type === CustomFieldType.String) {
            forEach(NUMBER_ATTRIBUTE_NAMES, (attribute) => {
                set(item, attribute, undefined);
            });
        }
    }, [item]);

    return (
        <div>
            <div className='tw-bg-layout tw-px-4 tw-py-2 tw-border-b tw-border-default'>
                <div className='tw-flex tw-items-start tw-gap-3 -md:tw-flex-col'>
                    <InputFormik
                        validate={!last}
                        name={`${path}.${FORM_FIELD_KEYS.NAME}`}
                        label={t(T_KEYS.LABELS.NAME)}
                        disabled={includes(READ_ONLY_FIELD_NAMES, item?.name)}
                        className='tw-w-full md:tw-flex-1'
                    />
                    <InputFormik
                        validate={!last}
                        name={`${path}.${FORM_FIELD_KEYS.TITLE}`}
                        label={t(T_KEYS.LABELS.LABEL)}
                        className='tw-w-full md:tw-flex-1'
                    />
                    <SelectFormik
                        name={`${path}.${FORM_FIELD_KEYS.TYPE}`}
                        placeholder={t(T_KEYS.LABELS.TYPE)}
                        options={typeOptions}
                        disabled={includes(READ_ONLY_FIELD_NAMES, item?.name)}
                        valueAs='id'
                        className='tw-w-full md:tw-flex-1'
                    />
                    {renderDeleteAction && (
                        <div className='tw-ml-auto tw-flex-shrink-0 tw-pt-4'>
                            {renderDeleteAction()}
                        </div>
                    )}
                </div>
            </div>

            {item && restAttributes.length > 0 && (
                <div className='tw-px-4 tw-py-2'>
                    {booleanRules.length > 0 && (
                        <div className='tw-flex tw-gap-6 tw-flex-wrap tw-mb-1'>
                            {map(booleanRules, renderRuleWithRemove)}
                        </div>
                    )}
                    <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1 tw-max-w-screen-sm'>
                        {map(numericRules, renderRuleWithRemove)}
                        {map(selectAndStringRules, renderRuleWithRemove)}
                        {map(enumRules, (attr: keyof typeof FORM_FIELD_KEYS) => {
                            const isReadonly = isReadOnlyAttribute(attr);
                            if (isUndefined(item[attr])) {
                                return null;
                            }
                            return (
                                <div key={attr} className='md:tw-col-span-2'>
                                    {renderAttributeFormElement(
                                        attr,
                                        isReadonly,
                                        !isReadonly ? () => onRemove(`${path}.${attr}`) : undefined,
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {availableValidationRuleOptions.length > 0 && (
                <div className='tw-px-4 tw-pb-3'>
                    <Select
                        placeholder={t(T_KEYS.ADD_VALIDATION_RULE)}
                        value=''
                        className='tw-w-full tw-max-w-screen-sm'
                        options={availableValidationRuleOptions}
                        valueAs='id'
                        onChange={handleAddAttributeDirectly}
                    />
                </div>
            )}
        </div>
    );
};
